import {
  EVENT_DRAFT_CREDIT_COST,
  EVENT_DRAFT_MAX_IMAGES,
  EVENT_PACKAGE_CREDIT_COST,
} from '@pulsify/shared/dto'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { History, ImagePlus, MessageCircle, X } from 'lucide-react'
import {
  type ClipboardEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { eventFlowApi } from '~/features/ai-studio/api/event-flow-api'
import {
  draftFailureAction,
  draftToHandoff,
  manualHandoff,
  socialNetworkOf,
} from '~/features/ai-studio/lib/event-handoff'
import { prepareUpload } from '~/features/listing-menus/lib/prepare-upload'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useLeaveGuard } from '~/features/shared/components/leave-guard'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { extractApiError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { ManualStart, ManualStartIcon } from '~/shared/ui/manual-start'
import { PromptField, PromptFieldAction } from '~/shared/ui/prompt-field'
import { Switch } from '~/shared/ui/switch'
import {
  type ChatSnapshot,
  clearAiFlowDraft,
  readAiFlowDraft,
  writeAiFlowDraft,
} from '../ai-flow/ai-flow-draft'
import { type ChatStart, EventAiChat } from '../ai-flow/event-ai-chat'
import { ConfirmButton } from '../tickets/confirm-button'
import { addPictures } from './pictures'

interface EventAiStartProps {
  businessId: string
  fromListing?: string
  onDrafted: (handoff: ReturnType<typeof draftToHandoff>) => void
}

export function EventAiStart({ businessId, fromListing, onDrafted: handOff }: EventAiStartProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [prompt, setPrompt] = useState('')
  const [pictures, setPictures] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [screenshotHint, setScreenshotHint] = useState<string | null>(null)
  const submittingRef = useRef(false)
  const packageId = useId()
  const [withPackage, setWithPackage] = useState(false)
  const [chatStart, setChatStart] = useState<ChatStart | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [restoredChat, setRestoredChat] = useState<ChatSnapshot | null>(null)
  const [restored, setRestored] = useState(false)
  const [chatInProgress, setChatInProgress] = useState(false)
  const draftLoaded = useRef(false)
  const draftFinished = useRef(false)
  const chatSnapshot = useRef<ChatSnapshot | null>(null)
  const startState = useRef({ prompt, pictures, withPackage })
  startState.current = { prompt, pictures, withPackage }
  const saveTimer = useRef<number | undefined>(undefined)
  const queryClient = useQueryClient()

  const persistDraft = useCallback(() => {
    window.clearTimeout(saveTimer.current)
    if (!draftLoaded.current || draftFinished.current) return
    writeAiFlowDraft(businessId, { ...startState.current, chat: chatSnapshot.current })
  }, [businessId])
  const schedulePersist = useCallback(() => {
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(persistDraft, 400)
  }, [persistDraft])

  useEffect(() => {
    let cancelled = false
    readAiFlowDraft(businessId).then((stored) => {
      if (cancelled) return
      draftLoaded.current = true
      if (!stored) return
      const current = startState.current
      if (!current.prompt && current.pictures.length === 0) {
        setPrompt(stored.prompt)
        setPictures(stored.pictures)
        setWithPackage(stored.withPackage)
      }
      if (stored.chat && stored.chat.machine.turns.length > 0) {
        chatSnapshot.current = stored.chat
        setRestoredChat(stored.chat)
        setChatInProgress(true)
        setChatStart({ id: 1, prompt: '', pictures: [] })
      }
      setRestored(true)
    })
    return () => {
      cancelled = true
    }
  }, [businessId])

  // biome-ignore lint/correctness/useExhaustiveDependencies: save whenever the start fields change
  useEffect(() => {
    schedulePersist()
  }, [prompt, pictures, withPackage, schedulePersist])

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') persistDraft()
    }
    window.addEventListener('pagehide', persistDraft)
    document.addEventListener('visibilitychange', flushWhenHidden)
    return () => {
      window.removeEventListener('pagehide', persistDraft)
      document.removeEventListener('visibilitychange', flushWhenHidden)
      persistDraft()
    }
  }, [persistDraft])

  const takeChatSnapshot = useCallback(
    (snapshot: ChatSnapshot) => {
      chatSnapshot.current = snapshot
      setChatInProgress(snapshot.machine.turns.length > 0)
      schedulePersist()
    },
    [schedulePersist],
  )

  const leaveGuard = useLeaveGuard(chatInProgress, m.leaveDraftKeptBody())

  const onDrafted = (handoff: Parameters<typeof handOff>[0]) => {
    draftFinished.current = true
    window.clearTimeout(saveTimer.current)
    leaveGuard.release()
    clearAiFlowDraft(businessId)
    handOff(handoff)
  }

  const discardDraft = () => {
    window.clearTimeout(saveTimer.current)
    chatSnapshot.current = null
    clearAiFlowDraft(businessId)
    setPrompt('')
    setPictures([])
    setWithPackage(false)
    setChatOpen(false)
    setChatStart(null)
    setRestoredChat(null)
    setChatInProgress(false)
    setRestored(false)
  }
  const draft = useMutation({
    mutationFn: (input: { prompt: string; pictures: File[]; locale: 'de' | 'en' }) =>
      eventFlowApi.draft(businessId, { ...input, package: false }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.CREDITS_BALANCE, businessId) }),
  })
  const price = withPackage ? EVENT_PACKAGE_CREDIT_COST : EVENT_DRAFT_CREDIT_COST
  const { state: cost } = useCreditCost(businessId, price)
  const previews = useMemo(() => pictures.map((file) => URL.createObjectURL(file)), [pictures])

  useEffect(
    () => () => {
      for (const url of previews) URL.revokeObjectURL(url)
    },
    [previews],
  )

  const canSubmit =
    (prompt.trim().length > 0 || pictures.length > 0) && cost !== 'short' && !submitting

  const add = (files: File[]) => {
    if (files.length === 0) return
    const result = addPictures(pictures, files)
    setPictures(result.pictures)
    if (result.pictures.length > pictures.length) setScreenshotHint(null)
    if (result.skipped > 0)
      toast.info(m.eventAiStartPicturesSkipped({ count: EVENT_DRAFT_MAX_IMAGES }))
  }

  const openChat = () => {
    setChatStart((current) => ({
      id: (current?.id ?? 0) + 1,
      prompt: prompt.trim(),
      pictures,
    }))
    setChatOpen(true)
    setPrompt('')
    setPictures([])
    setScreenshotHint(null)
  }

  const submit = async () => {
    if (!canSubmit || submittingRef.current) return
    if (withPackage) {
      openChat()
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    try {
      await prepareAndDraft()
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  const preparePictures = async (): Promise<File[]> => {
    const prepared = await Promise.all(pictures.map((file) => prepareUpload(file)))
    const ready: File[] = []
    for (const result of prepared) {
      if (result.ok) ready.push(result.file)
      else toast.error(m.eventAiStartPictureUnreadable({ name: result.name }))
    }
    return ready
  }

  const prepareAndDraft = async () => {
    const ready = await preparePictures()
    if (ready.length < pictures.length) return

    setScreenshotHint(null)
    try {
      const result = await draft.mutateAsync({
        prompt: prompt.trim(),
        pictures: ready,
        locale: getLocale() === 'en' ? 'en' : 'de',
      })
      onDrafted(draftToHandoff(result, toImageFiles(ready)))
    } catch (error) {
      const apiError = extractApiError(error)
      const action = draftFailureAction(apiError.errorCode, {
        prompt,
        pictureCount: ready.length,
      })
      if (action === 'fix-input') {
        toast.error(apiError.message)
      } else if (action === 'add-screenshot') {
        const hint = m.eventAiStartScreenshotHint({ network: socialNetworkOf(prompt) ?? '' })
        setScreenshotHint(hint)
        toast.info(hint)
      } else {
        toast.info(
          apiError.errorCode === 'EventPageUnreadable'
            ? m.eventAiStartFallbackUnreadable()
            : m.eventAiStartFallbackFailed(),
        )
        onDrafted(manualHandoff(prompt, toImageFiles(ready)))
      }
    }
  }

  const fillInManually = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      const ready = await preparePictures()
      onDrafted(manualHandoff(prompt, toImageFiles(ready)))
    } finally {
      submittingRef.current = false
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.files)
    if (files.length === 0) return
    event.preventDefault()
    add(files)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    add(Array.from(event.dataTransfer.files))
  }

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: paste and drop target around the inputs */}
      <div
        className="mx-auto flex min-h-[calc(100dvh-11rem)] w-full max-w-3xl flex-col items-center justify-center gap-7 pb-10 text-center"
        onPaste={handlePaste}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {leaveGuard.dialog}
        {restored ? (
          <div className="flex w-full max-w-[560px] flex-wrap items-center gap-2 rounded-lg border bg-muted px-4 py-2 text-left text-sm">
            <History aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">{m.eventDraftRestored()}</span>
            <ConfirmButton
              trigger={
                <Button type="button" variant="ghost" size="sm">
                  {m.eventDraftDiscard()}
                </Button>
              }
              title={m.eventDraftDiscardTitle()}
              body={m.eventDraftDiscardBody()}
              confirmLabel={m.eventDraftDiscard()}
              onConfirm={discardDraft}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={m.close()}
              onClick={() => setRestored(false)}
            >
              <X />
            </Button>
          </div>
        ) : null}
        <div className="flex flex-col gap-3">
          <h1 className="text-balance font-semibold text-4xl text-foreground leading-[1.05] tracking-[-0.03em] sm:text-5xl">
            {m.eventAiStartTitle()}
          </h1>
          <p className="mx-auto max-w-xl text-balance text-base text-muted-foreground">
            {m.eventAiStartSubtitle()}
          </p>
        </div>

        <div
          className={cn(
            'w-full rounded-[30px] transition-shadow motion-reduce:transition-none',
            dragging && 'ring-2 ring-brand ring-offset-4 ring-offset-background',
          )}
        >
          <PromptField
            value={prompt}
            onChange={setPrompt}
            onSubmit={() => submit()}
            label={m.eventAiStartPlaceholder()}
            submitLabel={m.eventAiStartSubmit()}
            placeholders={[
              m.eventAiStartPlaceholderLink(),
              m.eventAiStartPlaceholderSentence(),
              m.eventAiStartPlaceholder(),
            ]}
            canSubmit={canSubmit}
            busy={submitting}
            open={pictures.length > 0}
            actions={
              <>
                {pictures.length < EVENT_DRAFT_MAX_IMAGES && (
                  <PromptFieldAction
                    onClick={() => fileInputRef.current?.click()}
                    title={m.eventAiStartPicturesHint()}
                  >
                    <ImagePlus />
                    {m.eventAiStartAddPicture()}
                  </PromptFieldAction>
                )}
                <label
                  htmlFor={packageId}
                  title={m.eventAiStartPackageHint()}
                  onMouseDown={(event) => event.preventDefault()}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full px-2 py-1 text-[13px] text-muted-foreground hover:text-foreground"
                >
                  <Switch
                    id={packageId}
                    checked={withPackage}
                    onCheckedChange={(checked) => setWithPackage(checked === true)}
                  />
                  {m.eventAiStartPackageLabel()}
                </label>
                <CreditCostTag cost={price} className="text-[13px]" />
              </>
            }
          />
        </div>

        {screenshotHint && (
          <p role="status" className="max-w-[560px] text-balance text-muted-foreground text-sm">
            {screenshotHint}
          </p>
        )}

        {pictures.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-3">
            {pictures.map((file, index) => (
              <div
                key={previews[index]}
                className="relative size-20 overflow-hidden rounded-xl bg-muted"
              >
                <img src={previews[index]} alt={file.name} className="size-full object-cover" />
                <button
                  type="button"
                  aria-label={m.eventAiStartRemovePicture()}
                  onClick={() => setPictures(pictures.filter((_, i) => i !== index))}
                  className="absolute top-1 right-1 rounded-full bg-background/90 p-0.5 text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          hidden
          onChange={(event) => {
            add(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />

        <CreditCostNote businessId={businessId} cost={price} />

        {chatStart && !chatOpen && (
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-medium text-foreground text-sm outline-none transition-colors hover:border-brand/40 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MessageCircle className="size-4 text-brand" aria-hidden />
            {m.eventAiStartResume()}
          </button>
        )}

        <ManualStart>
          <Link
            to="/dashboard/events/create"
            search={{ manual: true, fromListing }}
            onClick={(event) => {
              if (!prompt.trim() && pictures.length === 0) return
              event.preventDefault()
              fillInManually()
            }}
          >
            <ManualStartIcon />
            {m.eventAiStartManual()}
          </Link>
        </ManualStart>

        <AiProcessingOverlay
          open={draft.isPending}
          title={m.eventAiStartWorkingTitle()}
          subtitle={m.eventAiStartWorkingSubtitle()}
        />
      </div>
      {chatStart && (
        <EventAiChat
          mode="event"
          businessId={businessId}
          open={chatOpen}
          onOpenChange={setChatOpen}
          start={chatStart}
          onDone={onDrafted}
          restore={restoredChat}
          onSnapshot={takeChatSnapshot}
        />
      )}
    </>
  )
}

function toImageFiles(files: File[]): ImageFile[] {
  return files.map((file, index) => ({
    file,
    preview: URL.createObjectURL(file),
    id: `ai-picture-${index}-${file.size}`,
  }))
}
